import { EXPLORE_ROUTES } from "../exploreRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import HeaderProfileSection from "../HeaderProfileSection";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useBlogList } from "../../hooks/useBlogList";
import { useBlogActions } from "../../hooks/useBlogActions";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import bookmark from "../../assets/images/bookmark.png";
import BlogFilter, { EMPTY_FILTERS } from "./BlogFilter";
import "./ArticleList.css";
import { CONTENT_ROUTES } from "../contentRoutes";

const ArticleCardShimmer = () => (
  <article className="qbl-questionCard" aria-hidden="true">
    <div className="qbl-questionCard__head">
      <span className="dashboard-skeleton dashboard-skeleton--button qbl-questionCard__shimmer-badge" />
      <span className="dashboard-skeleton dashboard-skeleton--icon qbl-questionCard__shimmer-bookmark" />
    </div>
    <span className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-line" />
    <span className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-line" />
    <div className="qbl-questionCard__footer">
      {Array.from({ length: 4 }).map((_, index) => <span key={index} className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-footer" />)}
    </div>
  </article>
);

const ArticleCard = ({ item, onOpen, onBookmark, onLike, isBookmarkUpdating, isLikeUpdating }) => (
  <article className="qbl-questionCard" role="button" tabIndex={0} onClick={() => onOpen(item)} onKeyDown={(event) => { if (event.key === "Enter") onOpen(item); }}>
    <div className="qbl-questionCard__head">
      <div className="qbl-questionCard__meta">
        <span className="qbl-questionCard__number">{String(item.serialNo).padStart(2, "0")}</span>
        {item.isRead ? <span className="article-card-status article-card-status--read"><i className="bi bi-check-circle-fill" />{item.readAt || "Read"}</span> : item.isViewed ? <span className="article-card-status"><i className="bi bi-eye-fill" />{item.viewedAt || "Viewed"}</span> : null}
      </div>
      <div>
        <button type="button" className="qbl-questionCard__bookmark" onClick={(event) => { event.stopPropagation(); onBookmark(item); }} disabled={isBookmarkUpdating}>{item.isBookmarked ? <img src={bookmark} alt="" /> : <i className="bi bi-bookmark" />}</button>
      </div>
    </div>

    <p className="qbl-questionCard__question">{item.title}</p>
    <p className="qbl-questionCard__descrip">{item.description}</p>

    <div className="qbl-questionCard__footer">
      <button type="button" className="qbl-questionCard__footerItem" onClick={(event) => { event.stopPropagation(); onLike(item); }} disabled={isLikeUpdating}><i className={`bi ${item.isLiked ? "bi-heart-fill" : "bi-heart"}`} aria-hidden="true" /><span>Like</span><strong>{item.likeCount}</strong></button>
      <span className="qbl-questionCard__footerItem"><i className="bi bi-eye" aria-hidden="true" /><span>View</span><strong>{item.viewCount}</strong></span>
      <span className="qbl-questionCard__footerItem"><i className="bi bi-journal-text" aria-hidden="true" /><span>Comments</span><strong>{item.commentCount}</strong></span>
    </div>
  </article>
);

const ArticleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: sidebarData = null } = useSidebarData();
  const initialContext = location.state?.articleListContext || {};
  const [searchTerm, setSearchTerm] = useState(initialContext.search || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialContext.search || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [allBlogs, setAllBlogs] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS, ...(initialContext.filters || {}) });
  const { toggleLike, toggleBookmark } = useBlogActions();

  const stateId = useMemo(
    () => resolveQuestionBankStateId({ sidebarData, locationState: location.state }),
    [location.state, sidebarData]
  );
  const filterParams = useMemo(() => ({
    is_mobile: 1,
    category_id: 8,
    ...(stateId > 0 ? { states_id: stateId } : {}),
  }), [stateId]);
  const queryParams = useMemo(() => ({
    is_mobile: 1,
    is_blog_list: 1,
    page: currentPage,
    category_id: 8,
    ...(stateId > 0 ? { states_id: stateId } : {}),
    ...(debouncedSearchTerm.trim() ? { search: debouncedSearchTerm.trim() } : {}),
    ...(appliedFilters.read_unread_id !== "" ? { read_unread_id: appliedFilters.read_unread_id } : {}),
    ...(appliedFilters.tag_id !== "" ? { tag_id: appliedFilters.tag_id } : {}),
    ...(appliedFilters.blog_category_id !== "" ? { blog_category_id: appliedFilters.blog_category_id } : {}),
    ...(appliedFilters.bookmark_status !== "" ? { bookmark_status: appliedFilters.bookmark_status } : {}),
  }), [appliedFilters, currentPage, debouncedSearchTerm, stateId]);

  const { data: blogData, isLoading, isFetching, isError, error } = useBlogList(queryParams);
  const { ref: loadMoreRef, inView } = useInView({ rootMargin: "240px" });
  const isPending = isLoading || isFetching;
  const isInitialPending = isPending && !allBlogs.length;
  const pagination = blogData?.pagination;
  const loadError = isError ? error?.response?.data?.message || error?.message || "Unable to load articles." : "";
  const titleParts = String(blogData?.categoryName || "General Hindi").trim().split(/\s+/);
  const titleFirst = titleParts.slice(0, -1).join(" ") || titleParts[0];
  const titleLast = titleParts.length > 1 ? titleParts.at(-1) : "";

  const updateArticle = (articleId, update) => {
    setAllBlogs((articles) => articles.map((article) => Number(article.id) === Number(articleId) ? { ...article, ...update(article) } : article));
  };

  const showActionError = (error) => Swal.fire({
    icon: "error",
    title: "Action unavailable",
    text: error?.response?.data?.message || error?.message || "Please try again.",
  });

  const handleLike = async (article) => {
    if (toggleLike.isPending) return;
    try {
      const result = await toggleLike.mutateAsync({ blog_id: article.id });
      updateArticle(article.id, () => ({ isLiked: Number(result?.like_status) === 1, likeCount: Number(result?.total_likes || 0) }));
    } catch (error) {
      showActionError(error);
    }
  };

  const handleBookmark = async (article) => {
    if (toggleBookmark.isPending) return;
    try {
      const result = await toggleBookmark.mutateAsync({ blog_id: article.id });
      updateArticle(article.id, () => ({ isBookmarked: Number(result?.bookmark_status) === 1 }));
    } catch (error) {
      showActionError(error);
    }
  };

  const handleBackNavigation = () => {
    const backTarget = location.state?.articlesBackTarget;
    if (backTarget?.pathname) {
      navigate(backTarget.pathname, { replace: true, state: backTarget.state });
      return;
    }

    navigate(EXPLORE_ROUTES.categories, {
      replace: true,
      state: {
        selectedState: location.state?.selectedState,
      },
    });
  };

  const persistListContext = (filters, search) => {
    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        articleListContext: { filters, search },
      },
    });
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    persistListContext(filters, debouncedSearchTerm);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setAppliedFilters(EMPTY_FILTERS);
    persistListContext(EMPTY_FILTERS, "");
  };

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => window.clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters, debouncedSearchTerm]);

  useEffect(() => {
    const nextBlogs = blogData?.blogs || [];

    setAllBlogs((currentBlogs) => {
      if (currentPage === 1) {
        return nextBlogs;
      }

      const knownIds = new Set(currentBlogs.map((blog) => blog.id));
      return [...currentBlogs, ...nextBlogs.filter((blog) => !knownIds.has(blog.id))];
    });
  }, [blogData?.blogs, currentPage]);

  useEffect(() => {
    const totalPages = Number(pagination?.totalPages || 1);

    if (inView && !isFetching && !isLoading && !loadError && currentPage < totalPages) {
      setCurrentPage((page) => page + 1);
    }
  }, [currentPage, inView, isFetching, isLoading, loadError, pagination?.totalPages]);

  useEffect(() => {
    if (!isFilterOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsFilterOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isFilterOpen]);

  return (
    <>
      <style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }`}</style>
      <HomeDashboardLayout showHeader={false}>
      <div className="qbl-page qbankList-page">
        <div className="qbl-main">
          <section className="qbl-panel">
            <div className="hideProfile-page ">
              <div className="categories-page-header mb-0">
                <div><button type="button" className="categories-back-button" onClick={handleBackNavigation} aria-label="Go back"><i className="bi bi-chevron-left" /></button></div>
                <div><h4 className="d-flex d-sm-none categories-page-title" /></div>
                <div><HeaderProfileSection /></div>
              </div>
            </div>

            <div className="qb-body"><div className="d-flex align-items-center justify-content-center"><div className="qb-copy">
              <div className="qb-loc"><i className="bi bi-geo-alt-fill" /><span> {blogData?.stateName || "Himachal Pradesh"}</span></div>
              <h1 className="qb-title">{titleFirst}{titleLast ? <span>{` ${titleLast}`}</span> : null}</h1>
              <p className="qb-sub qbb-list">Practice more , score higher !</p>
            </div></div></div>

            <div className="d-flex d-xl-block align-items-center mobile-filter-box">
              <label className="qbl-search"><i className="bi bi-search" aria-hidden="true" /><input type="text" placeholder="Search questions..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} /></label>
              <button type="button" className="mobile-Filter-icon d-flex d-lg-flex d-xl-none" onClick={() => setIsFilterOpen(true)} aria-label="Open filters"><span className="filter-card__icon"><i className="bi bi-sliders2" /></span></button>
            </div>

            <div className="qbl-summaryRow"><h2>{isPending ? "Loading articles..." : `${Number(pagination?.totalRecords || 0)} Result Found`}</h2></div>

            <div className="qbl-questionList" aria-label="Article list">
              {isInitialPending ? Array.from({ length: 6 }).map((_, index) => <ArticleCardShimmer key={index} />) : null}
              {!isPending && loadError ? <div className="choose-state__status choose-state__status--error">{loadError}</div> : null}
              {!isPending && !loadError && !allBlogs.length ? <div className="choose-state__status">No articles found.</div> : null}
              {!loadError ? allBlogs.map((item) => <ArticleCard key={item.id} item={item} onOpen={(article) => navigate(CONTENT_ROUTES.articleDetail, { state: { ...location.state, blogId: article.id, state_id: stateId, articleListContext: { filters: appliedFilters, search: debouncedSearchTerm }, articlesBackTarget: { pathname: CONTENT_ROUTES.articles, state: { ...location.state, articleListContext: { filters: appliedFilters, search: debouncedSearchTerm } } } } })} onLike={handleLike} onBookmark={handleBookmark} isLikeUpdating={toggleLike.isPending && Number(toggleLike.variables?.blog_id) === Number(item.id)} isBookmarkUpdating={toggleBookmark.isPending && Number(toggleBookmark.variables?.blog_id) === Number(item.id)} />) : null}
              {currentPage > 1 && isPending ? <ArticleCardShimmer /> : null}
              {!loadError && currentPage < Number(pagination?.totalPages || 1) ? <div ref={loadMoreRef} aria-hidden="true" /> : null}
            </div>
          </section>
        </div>
        <div className={`qbl-filterBackdrop${isFilterOpen ? " is-open" : ""}`} onClick={() => setIsFilterOpen(false)} aria-hidden="true" />
        <BlogFilter isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} onApply={handleApplyFilters} onReset={handleResetFilters} params={filterParams} appliedFilters={appliedFilters} />
      </div>
      </HomeDashboardLayout>
    </>
  );
};

export default ArticleList;
